import Swal from 'sweetalert2';
import { getState, modifyState } from '~src/state';
import { getAxiosClient } from '~src/api';
import { updateEditorCanvasPage } from '~graphql/controllers/editorCanvasPages';
import { convertNextPages, hasNextCanvasPages } from './convertNextCanvasJson';

const escapeHtml = (text) =>
  String(text).replace(
    /[&<>"']/g,
    (char) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[char])
  );

const lossesHtml = (losses) =>
  losses.length === 0
    ? ''
    : `<p class="mt-3 mb-1"><b>Cosa non si può portare in Giotto classico:</b></p>
       <ul class="text-left">${losses
         .map((loss) => `<li>${escapeHtml(loss)}</li>`)
         .join('')}</ul>`;

const saveProjectPage = (page) =>
  updateEditorCanvasPage(page.objectId, { canvasJson: page.canvasJson });

const saveTemplatePage = (tableName) => async (page) => {
  const httpClient = await getAxiosClient();
  return httpClient({
    url: `${process.env.PARSE_API_URL}/${tableName}/${page.objectId}`,
    method: 'PUT',
    data: { data: { canvasJson: page.canvasJson } },
    responseType: 'json',
  });
};

export const templatePagesTable = (template) =>
  template && template.isGlobal
    ? 'editor_templates_default_pages'
    : 'editor_templates_pages';

/**
 * Se le pagine in stato vengono da Giotto Next (Fabric 7), avvisa l'utente e
 * gli offre di convertirle. La conversione riscrive le pagine salvate, così il
 * progetto (o il modello) resta nel formato classico anche per le anteprime.
 *
 * @param {{ kind: 'project' | 'template', templateTable?: string }} options
 * @returns {Promise<boolean>} false se l'utente rinuncia: non va aperto.
 */
export const ensureLegacyPages = async ({ kind, templateTable }) => {
  const pages = getState('pages', []);
  if (!hasNextCanvasPages(pages)) return true;

  const { pages: converted, losses } = convertNextPages(pages);
  const savePage =
    kind === 'template' ? saveTemplatePage(templateTable) : saveProjectPage;
  const subject = kind === 'template' ? 'Questo modello' : 'Questo progetto';

  const result = await Swal.fire({
    icon: 'warning',
    title: 'Creato con Giotto 2.0',
    html: `<p>${subject} è stato creato o modificato con Giotto 2.0, che salva in un formato diverso da Giotto classico.</p>
      <p>Per aprirlo qui va convertito. La conversione modifica ${
        kind === 'template' ? 'il modello' : 'il progetto'
      }: anche in Giotto 2.0 si vedrà la versione convertita.</p>
      ${lossesHtml(losses)}`,
    showCancelButton: true,
    confirmButtonText: 'Converti e apri',
    cancelButtonText: 'Torna indietro',
    confirmButtonColor: '#278e8e',
    allowOutsideClick: false,
    showLoaderOnConfirm: true,
    preConfirm: async () => {
      try {
        for (let index = 0; index < converted.length; index++) {
          if (converted[index] === pages[index]) continue;
          if (converted[index].objectId) await savePage(converted[index]);
        }
        return true;
      } catch (error) {
        console.error('Next canvas conversion failed', error);
        Swal.showValidationMessage(
          'Conversione non riuscita: riprova o contatta il supporto.'
        );
        return false;
      }
    },
  });

  if (!result.isConfirmed) return false;

  await modifyState('pages', converted);
  return true;
};

/**
 * Pagine di un modello usato per creare o cambiare un progetto: si convertono
 * senza chiedere, perché il modello resta intatto e le pagine diventano del
 * nuovo progetto.
 */
export const convertTemplatePagesForUse = (pages = []) => {
  if (!hasNextCanvasPages(pages)) return pages;
  const { pages: converted, losses } = convertNextPages(pages);
  if (losses.length > 0) {
    console.warn('Giotto 2.0 template converted with losses', losses);
  }
  return converted;
};
